import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseFectch';

export interface AnalyticsTrend {
  _id: string;
  presentDays?: number;
  avgHours?: number;
  totalHours?: number;
  leaveCount?: number;
  paidLeaveCount?: number;
  unpaidLeaveCount?: number;
  hires?: number;
  activeHires?: number;
  inactive?: number;
  turnoverRate?: number;
}

export interface AnalyticsTrendResponse {
  success: boolean;
  data: AnalyticsTrend[];
}

export interface AdminDashboardData {
  headcount: { total: number; active: number; newThisMonth: number };
  attendance: { presentToday: number; monthlyLogs: number };
  leave: { pending: number; approved: number };
  grievances: { pending: number; approved: number };
  training: { overdueCount: number; complianceRate: number };
  notifications: { total: number; unread: number };
}

export interface ManagerDashboardData {
  department?: string;
  headcount: number;
  attendanceToday: number;
  leaveRequests: { pending: number };
  grievances: { pending: number };
  training: { overdueCount: number };
}

export interface DashboardResponse {
  success: boolean;
  data: AdminDashboardData | ManagerDashboardData;
}

export interface UtilizationData {
  utilizationRate: number;
  billableHours: number;
  totalHours: number;
}

export interface TrainingComplianceRate {
  trainingType: string;
  total: number;
  completed: number;
  overdue: number;
  complianceRate: number;
}

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AnalyticsTrends',
    'AdminDashboard',
    'ManagerDashboard',
    'TrainingCompliance',
    'Utilization',
  ],
  endpoints: (builder) => ({
    getAttendanceTrends: builder.query<AnalyticsTrendResponse, { period?: string; months?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.period) params.append('period', args.period);
        if (args?.months) params.append('months', String(args.months));
        const qs = params.toString();
        return {
          url: `/analytics/insights/trends/attendance${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['AnalyticsTrends'],
    }),

    getLeaveSeasonality: builder.query<AnalyticsTrendResponse, { period?: string; months?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.period) params.append('period', args.period);
        if (args?.months) params.append('months', String(args.months));
        const qs = params.toString();
        return {
          url: `/analytics/insights/trends/leave${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['AnalyticsTrends'],
    }),

    getHiringVelocity: builder.query<AnalyticsTrendResponse, { months?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.months) params.append('months', String(args.months));
        const qs = params.toString();
        return {
          url: `/analytics/insights/trends/hiring${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['AnalyticsTrends'],
    }),

    getTurnoverRate: builder.query<AnalyticsTrendResponse, { months?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.months) params.append('months', String(args.months));
        const qs = params.toString();
        return {
          url: `/analytics/insights/trends/turnover${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['AnalyticsTrends'],
    }),

    getAdminDashboard: builder.query<DashboardResponse, void>({
      query: () => ({
        url: '/dashboard/admin',
        method: 'GET',
      }),
      providesTags: ['AdminDashboard'],
    }),

    getManagerDashboard: builder.query<DashboardResponse, void>({
      query: () => ({
        url: '/dashboard/manager',
        method: 'GET',
      }),
      providesTags: ['ManagerDashboard'],
    }),

    getTrainingComplianceRate: builder.query<{ success: boolean; data: TrainingComplianceRate[] }, void>({
      query: () => ({
        url: '/training/compliance-rate',
        method: 'GET',
      }),
      providesTags: ['TrainingCompliance'],
    }),

    getUtilizationRate: builder.query<{ success: boolean; data: UtilizationData }, { employeeId?: string; startDate?: string; endDate?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.employeeId) params.append('employeeId', args.employeeId);
        if (args?.startDate) params.append('startDate', args.startDate);
        if (args?.endDate) params.append('endDate', args.endDate);
        const qs = params.toString();
        return {
          url: `/agency/utilization${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Utilization'],
    }),
  }),
});

export const {
  useLazyGetAttendanceTrendsQuery,
  useLazyGetLeaveSeasonalityQuery,
  useLazyGetHiringVelocityQuery,
  useLazyGetTurnoverRateQuery,
  useLazyGetAdminDashboardQuery,
  useLazyGetManagerDashboardQuery,
  useLazyGetTrainingComplianceRateQuery,
  useLazyGetUtilizationRateQuery,
} = analyticsApi;

export default analyticsApi;